import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import { Checkbox } from 'primereact/checkbox';
import { Button } from '../../../components/ui/Button';
import { ProgressSpinner } from 'primereact/progressspinner';

import { setAssign, setMediaIdReset, toggleDialog } from '../../../redux/Slices/Global/globalSlice';
import { useInvalidateCampaignLayer } from '../../../queries/utils';
import { useCustomToast } from '../../Contexts/ToastContext';
import { searchYoutube, addYoutubeToContent } from '../../../queries/youtube';
import { useMediaPreviewOptional } from '../MediaPreview/MediaPreviewContext';

const PAGE_SIZE = 20;

function formatDuration(seconds) {
    const s = Number(seconds) || 0;
    if (s <= 0) return '';
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(r)}` : `${m}:${pad(r)}`;
}

function YouTubeSearchList({ builderRow = false }) {
    const { t } = useTranslation();
    // `query` is what's typed; `committed` is what's actually been searched.
    // Spec: search only fires on Enter or button click — NOT on keystroke.
    const [query, setQuery] = useState('');
    const [committed, setCommitted] = useState('');
    const [results, setResults] = useState([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [selectedVideos, setSelectedVideos] = useState({});
    const [isSearching, setIsSearching] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [searchError, setSearchError] = useState(null);

    const reqIdRef = useRef(0);

    const { assign, layer } = useSelector((state) => state.global);
    const screenDetails = useSelector((state) => state.layout.screenDetails);
    const dispatch = useDispatch();
    const { id } = useParams();
    const queryClient = useQueryClient();
    const invalidateCampaignLayer = useInvalidateCampaignLayer();
    const { showToast } = useCustomToast();
    // Null outside a <MediaPreviewProvider> — see that hook's own doc
    // comment; InteractiveCampaignSetup.jsx/ScreenInfo.jsx render this
    // component with builderRow=false and never mount the provider.
    const mediaPreview = useMediaPreviewOptional();

    const runSearch = async () => {
        const trimmed = query.trim();
        if (!trimmed || isSearching) return;
        const myReq = ++reqIdRef.current;
        setCommitted(trimmed);
        setIsSearching(true);
        setSearchError(null);
        setResults([]);
        setVisibleCount(PAGE_SIZE);
        try {
            const data = await searchYoutube(trimmed);
            if (myReq !== reqIdRef.current) return; // stale response — ignore
            setResults(data || []);
        } catch {
            if (myReq !== reqIdRef.current) return;
            setSearchError(t('addcontent.search_failed'));
            setResults([]);
        } finally {
            if (myReq === reqIdRef.current) setIsSearching(false);
        }
    };

    const clearAll = () => {
        reqIdRef.current++;
        setQuery('');
        setCommitted('');
        setResults([]);
        setSelectedVideos({});
        setSearchError(null);
        setVisibleCount(PAGE_SIZE);
    };

    // Sync with global ADD button (AddContentPopup footer)
    useEffect(() => {
        if (assign && Object.keys(selectedVideos).length > 0) {
            handleAdd();
        } else if (assign) {
            dispatch(setAssign(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assign]);

    // `videosOverride` lets the lightbox preview's own "إضافة إلى المحتوى"
    // button add a single video directly (prototype's `ytAdd`, ~line 961),
    // independent of whatever is currently multi-selected in the grid —
    // same orchestration/invalidation path either way, just a different
    // source list.
    const handleAdd = async (videosOverride) => {
        // `onClick={handleAdd}` elsewhere passes the click SyntheticEvent as
        // the first arg — Array.isArray() guards against treating that
        // event as a videos array (a plain `videosOverride ||` fallback
        // would not, since an event object is truthy).
        const videos = Array.isArray(videosOverride) ? videosOverride : Object.values(selectedVideos);
        if (!videos.length || isAdding) return;

        setIsAdding(true);
        try {
            const payload = { videos, layer };

            if (window.location.pathname.includes('campaign')) {
                payload.campaign_id = id;
            } else if (window.location.pathname.includes('layout')) {
                payload.campaign_id = screenDetails?.id;
            } else {
                payload.playlist_id = id;
            }

            const result = await addYoutubeToContent(payload);

            dispatch(setMediaIdReset(result?.toRefetch));
            dispatch(toggleDialog('addContent'));

            if (window.location.pathname.includes('playlist')) {
                queryClient.invalidateQueries(['getPlaylist', id]);
                queryClient.invalidateQueries(['getPlaylistLayerContent', id, layer]);
                queryClient.invalidateQueries({ queryKey: ['companyPlaylists'] });
            } else {
                invalidateCampaignLayer();
            }
        } catch (err) {
            showToast('error', 'YouTube', err?.response?.data?.msg || 'Failed to add videos. Please try again.');
        } finally {
            setIsAdding(false);
            dispatch(setAssign(false));
        }
    };

    const toggleVideo = (video) => {
        setSelectedVideos((prev) => {
            const next = { ...prev };
            if (next[video.id]) delete next[video.id];
            else next[video.id] = video;
            return next;
        });
    };

    // v15 media preview — "معاينة" opens a REAL embedded YouTube iframe
    // (autoplay=0) inside the shared lightbox, with its own bottom-bar
    // "إضافة إلى المحتوى" button that adds this ONE video directly and
    // closes (prototype's ytView(), ~pp-campaign-builder-prototype.html:
    // 956-965). Reuses this component's own already-fetched `results` — no
    // new fetch, no new video-id source.
    const openPreview = (video) => {
        if (!mediaPreview) return;
        const list = visible.map((v) => ({
            type: 'YOUTUBE',
            youtubeId: v.id,
            title: v.title,
            duration: v.duration,
        }));
        const idx = visible.findIndex((v) => v.id === video.id);
        mediaPreview.openLightbox(list, idx < 0 ? 0 : idx, {
            addLabel: t('addcontent.youtube_add_to_content'),
            onAdd: (o) => {
                const picked = results.find((v) => v.id === o.youtubeId);
                if (picked) handleAdd([picked]);
            },
        });
    };

    const selectedCount = Object.keys(selectedVideos).length;
    const hasCommitted = committed.length > 0;
    const visible = results.slice(0, visibleCount);
    const canLoadMore = visibleCount < results.length;

    // ── Campaign builder add-content sheet — .pp-cb-field search bar +
    // .pp-cb-yt-row result rows, ported from the prototype. Real search/
    // multi-select/add behavior is UNCHANGED (kept — genuine, already-
    // shipped functionality the static mockup's single-row demo didn't
    // model); only the visual chrome is ported.
    if (builderRow) {
        return (
            <div className="d-flex flex-column">
                <div className="pp-cb-field">
                    <input
                        type="text"
                        placeholder={t('addcontent.youtube_search_placeholder')}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }}
                        autoFocus
                    />
                    <Button
                        icon="pi pi-search"
                        label={t('buttons.search')}
                        onClick={runSearch}
                        disabled={!query.trim() || isSearching}
                        loading={isSearching}
                    />
                </div>

                {isSearching && (
                    <div className="py-5 text-center w-100"><ProgressSpinner style={{ width: 40, height: 40 }} strokeWidth="3" /></div>
                )}
                {!isSearching && !hasCommitted && (
                    <div className="pp-cb-hint"><i className="pi pi-info-circle" /><span>{t('addcontent.youtube_search_hint')}</span></div>
                )}
                {!isSearching && searchError && (
                    <div className="py-4 text-center w-100" style={{ color: 'var(--pp-btn-coral)' }}>{searchError}</div>
                )}
                {!isSearching && hasCommitted && !searchError && results.length === 0 && (
                    <div className="py-4 text-center w-100 opacity-70">{t('messages.no_results')}</div>
                )}
                {!isSearching && visible.map((video) => {
                    const isSelected = !!selectedVideos[video.id];
                    const dur = formatDuration(video.duration);
                    return (
                        <div
                            key={video.id}
                            className="pp-cb-yt-row"
                            style={isSelected ? { borderColor: 'var(--pp-btn-honey)', boxShadow: '0 0 0 1.5px var(--pp-btn-honey) inset' } : undefined}
                            onClick={() => toggleVideo(video)}
                        >
                            <span className="yt-th">
                                {video.thumbnail ? <img src={video.thumbnail} alt={video.title} /> : <i className="pi pi-video" />}
                            </span>
                            <span className="yt-t">{video.title}</span>
                            {dur && <span className="yt-d">{dur}</span>}
                            {mediaPreview && (
                                <button
                                    type="button"
                                    className="yt-pv"
                                    onClick={(e) => { e.stopPropagation(); openPreview(video); }}
                                >
                                    {t('addcontent.youtube_preview_label')}
                                </button>
                            )}
                            <button
                                type="button"
                                className="yt-add"
                                aria-label={t('addcontent.add_videos', { count: 1 })}
                                onClick={(e) => { e.stopPropagation(); handleAdd([video]); }}
                            >
                                <i className="pi pi-plus" />
                            </button>
                        </div>
                    );
                })}
                {!isSearching && canLoadMore && (
                    <div className="text-center mt-3">
                        <Button
                            icon="pi pi-chevron-down"
                            label={t('addcontent.load_more', { count: Math.min(PAGE_SIZE, results.length - visibleCount) })}
                            onClick={() => setVisibleCount((n) => Math.min(n + PAGE_SIZE, results.length))}
                            outlined
                        />
                    </div>
                )}
                {selectedCount > 0 && (
                    <div className="mt-3">
                        <Button
                            icon="pi pi-plus"
                            label={t('addcontent.add_videos', { count: selectedCount })}
                            loading={isAdding}
                            className="w-100"
                            onClick={handleAdd}
                        />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="d-flex flex-column" style={{ minHeight: '50vh' }}>
            {/* Search bar — Enter or button to submit. NO debounce. */}
            <div className="w-full my-3 px-3">
                <div className="d-flex gap-2">
                    <div className="relative flex-1">
                        <input
                            type="text"
                            className="form-control h-[45px] w-full"
                            placeholder={t('addcontent.youtube_search_placeholder')}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    runSearch();
                                }
                            }}
                            autoFocus
                        />
                        {query && !isSearching && (
                            <button
                                type="button"
                                onClick={clearAll}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                                title={t('buttons.clear')}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                    <Button
                        icon="pi pi-search"
                        label={t('buttons.search')}
                        onClick={runSearch}
                        disabled={!query.trim() || isSearching}
                        loading={isSearching}
                        className="btn btn-primary"
                    />
                </div>
            </div>

            <hr className="w-100 mt-0" />

            {/* Empty / loading / error / results */}
            {isSearching && (
                <div className="py-5 text-center w-100">
                    <ProgressSpinner style={{ width: 48, height: 48 }} strokeWidth="3" />
                </div>
            )}

            {!isSearching && !hasCommitted && (
                <h5 className="py-5 text-center text-muted w-100">
                    {t('addcontent.youtube_search_hint')}
                </h5>
            )}

            {!isSearching && searchError && (
                <h5 className="py-5 text-center text-danger w-100">{searchError}</h5>
            )}

            {!isSearching && hasCommitted && !searchError && results.length === 0 && (
                <h5 className="py-5 text-center w-100">{t('messages.no_results')}</h5>
            )}

            {!isSearching && results.length > 0 && (
                <div className="px-3 overflow-auto flex-1">
                    <div className="d-grid gap-3 mb-3" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                        {visible.map((video) => {
                            const isSelected = !!selectedVideos[video.id];
                            const dur = formatDuration(video.duration);
                            return (
                                <div
                                    key={video.id}
                                    onClick={() => toggleVideo(video)}
                                    className={`rounded border cursor-pointer p-1 ${isSelected ? 'pp-selected-border bg-blue-50' : 'border-gray-200 hover:border-gray-400'}`}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div style={{ position: 'relative' }}>
                                        <img
                                            src={video.thumbnail}
                                            alt={video.title}
                                            className="w-full rounded mb-1"
                                            style={{ aspectRatio: '16/9', objectFit: 'cover', display: 'block' }}
                                            loading="lazy"
                                            onError={(e) => {
                                                e.target.src = `https://img.youtube.com/vi/${video.id}/mqdefault.jpg`;
                                            }}
                                        />
                                        {dur && (
                                            <span
                                                style={{
                                                    position: 'absolute',
                                                    right: 4,
                                                    bottom: 8,
                                                    background: 'rgba(0,0,0,0.8)',
                                                    color: '#fff',
                                                    padding: '1px 5px',
                                                    borderRadius: 3,
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {dur}
                                            </span>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-start gap-1 mb-1">
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={() => toggleVideo(video)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <p
                                            className="text-xs m-0 fw-medium"
                                            style={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                fontSize: 12,
                                            }}
                                            title={video.title}
                                        >
                                            {video.title}
                                        </p>
                                    </div>
                                    {video.channel && (
                                        <p className="m-0 text-muted" style={{ fontSize: 11 }}>
                                            {video.channel}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            {t('addcontent.showing_of', { shown: visible.length, total: results.length })}
                        </span>
                        {canLoadMore && (
                            <Button
                                icon="pi pi-chevron-down"
                                label={t('addcontent.load_more', { count: Math.min(PAGE_SIZE, results.length - visibleCount) })}
                                onClick={() => setVisibleCount((n) => Math.min(n + PAGE_SIZE, results.length))}
                                className="btn btn-outline-primary btn-sm"
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Sticky add button */}
            {selectedCount > 0 && (
                <div
                    className="px-3 py-2 bg-white border-top"
                    style={{ position: 'sticky', bottom: 0 }}
                >
                    <Button
                        icon="pi pi-plus"
                        label={t('addcontent.add_videos', { count: selectedCount })}
                        loading={isAdding}
                        className="btn btn-primary w-100 text-center fs-6 p-2"
                        onClick={handleAdd}
                    />
                </div>
            )}
        </div>
    );
}

export default YouTubeSearchList;
